export const PROPERTY_QUALIFIER_SCHEMA_VERSION = 'property-qualifier.v1' as const;

export const PROPERTY_QUALIFIER_STAGES = [
  'resolving_zip',
  'researching_official_sources',
  'validating_evidence',
] as const;

export const PROPERTY_QUALIFIER_STATUSES = [
  'complete',
  'partial',
  'unavailable',
] as const;

export const PROPERTY_QUALIFIER_MESSAGE_KEYS = [
  'adu_standard',
  'generational_living',
  'imagine_possibility',
  'lifestyle_studio',
  'potential_unlocked',
  'all_roads_lead_home',
] as const;

// Single flow-registry version, mirroring REPORT_FLOW_VERSION. The (key,
// version) pair is validated atomically: a request carrying a registered key at
// an unregistered version is rejected outright rather than sanitized.
export const PROPERTY_QUALIFIER_FLOW_VERSION = 'v1' as const;

// Flows that may run a ZIP-area Property Check. This is the client mirror of the
// Orca-owned registry in `app/src/contexts/ReportConversionFlowContext.jsx`,
// duplicated as a literal because that registry is a `.jsx` module and this file
// is typechecked without `allowJs`.
//
// `purchase_first_flow` is deliberately ABSENT. Variant D is the public default
// and never mounts the check at all (`HeroStage` renders `HeroRunSequence` only
// when `!isPurchaseFirst`), so a Variant D request reaching this client is a
// wiring defect, not a supported arm — it fails closed here too rather than
// spending a live call and minting an unjoinable attribution row.
export const PROPERTY_QUALIFIER_FLOW_KEYS = [
  'direct_checkout_flow',
  'lead_first_flow',
  'welcome_gate_flow',
] as const;

// The scope note is a fixed disclosure, not free copy: it is the one sentence
// that bounds what a ZIP-area check claims. Pinned to the server contract
// (`ZIP_AREA_SCOPE_NOTE` in `functions/src/orca/flows/propertyQualifier.ts`) so a
// response cannot quietly widen its own scope.
export const PROPERTY_QUALIFIER_SCOPE_NOTE =
  'General ZIP-area guidance only; no parcel or permit review was performed.';

// Pinned to the server contract (`PROPERTY_QUALIFIER_MAX_SUMMARY_LENGTH` in
// functions/src/orca/flows/propertyQualifier.ts) so client-side validation can
// never silently drift looser than what the backend actually enforces.
export const PROPERTY_QUALIFIER_MAX_SUMMARY_LENGTH = 900;

export type PropertyQualifierStage = typeof PROPERTY_QUALIFIER_STAGES[number];
export type PropertyQualifierStatus = typeof PROPERTY_QUALIFIER_STATUSES[number];
export type PropertyQualifierMessageKey = typeof PROPERTY_QUALIFIER_MESSAGE_KEYS[number];
export type PropertyQualifierFlowKey = typeof PROPERTY_QUALIFIER_FLOW_KEYS[number];
export type PropertyTeaserMode = 'synthetic' | 'live';

export interface PropertyQualifierRequest {
  schemaVersion: typeof PROPERTY_QUALIFIER_SCHEMA_VERSION;
  requestId: string;
  zipCode: string;
  messageKey: PropertyQualifierMessageKey;
  flowKey: PropertyQualifierFlowKey;
  flowVersion: typeof PROPERTY_QUALIFIER_FLOW_VERSION;
}

export interface PropertyQualifierSource {
  id: string;
  publisher: string;
  title: string;
  url: string;
  retrievedAt: string;
}

export interface PropertyQualifierResponse {
  schemaVersion: typeof PROPERTY_QUALIFIER_SCHEMA_VERSION;
  requestId: string;
  status: PropertyQualifierStatus;
  zipCode: string;
  messageKey: PropertyQualifierMessageKey;
  flowKey: PropertyQualifierFlowKey;
  flowVersion: typeof PROPERTY_QUALIFIER_FLOW_VERSION;
  result: {
    locationLabel: string;
    headline: string;
    summary: string;
    highlights: string[];
    messageMatchedInsight: string | null;
    nextStep: string;
    scopeNote: string;
  };
  sources: PropertyQualifierSource[];
  limits: {
    propertySpecific: false;
    parcelVerified: false;
    eligibilityDetermined: false;
    feasibilityDetermined: false;
  };
  meta?: {
    executionMode?: PropertyTeaserMode;
  };
}

export interface PropertyTeaserRunOptions {
  signal?: AbortSignal;
  onStage?: (stage: PropertyQualifierStage) => void;
  syntheticStageDelayMs?: number;
}

export interface PropertyTeaserClient {
  mode: PropertyTeaserMode;
  run(
    input: Omit<PropertyQualifierRequest, 'schemaVersion' | 'requestId'> & { requestId?: string },
    options?: PropertyTeaserRunOptions,
  ): Promise<PropertyQualifierResponse>;
}

type FetchLike = typeof fetch;

export type PropertyTeaserAppCheckTokenProvider = () => Promise<string | null>;

type PropertyTeaserClientConfig = {
  mode?: PropertyTeaserMode;
  endpoint?: string;
  fetchImpl?: FetchLike;
  appCheckTokenProvider?: PropertyTeaserAppCheckTokenProvider;
};

const ZIP_CODE_PATTERN = /^[0-9]{5}$/;
const SAFE_ENDPOINT_PATTERN = /^\/(?:[A-Za-z0-9._~-]+\/)*[A-Za-z0-9._~-]+$/;
const MAX_APP_CHECK_TOKEN_LENGTH = 8 * 1024;

export class PropertyTeaserClientError extends Error {
  readonly code: 'INVALID_INPUT' | 'REQUEST_FAILED' | 'INVALID_RESPONSE';

  constructor(
    code: PropertyTeaserClientError['code'],
    message: string,
  ) {
    super(message);
    this.name = 'PropertyTeaserClientError';
    this.code = code;
  }
}

export function normalizeZipCode(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const normalized = value.trim();
  return ZIP_CODE_PATTERN.test(normalized) ? normalized : null;
}

function isPropertyQualifierMessageKey(value: unknown): value is PropertyQualifierMessageKey {
  return typeof value === 'string'
    && PROPERTY_QUALIFIER_MESSAGE_KEYS.includes(value as PropertyQualifierMessageKey);
}

function isPropertyQualifierFlowKey(value: unknown): value is PropertyQualifierFlowKey {
  return typeof value === 'string'
    && PROPERTY_QUALIFIER_FLOW_KEYS.includes(value as PropertyQualifierFlowKey);
}

export function createPropertyQualifierRequestId(): string {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `property-check-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function normalizeAppCheckToken(value: unknown): string | null {
  if (typeof value !== 'string' || value.length > MAX_APP_CHECK_TOKEN_LENGTH) return null;
  const token = value.trim();
  if (!token || /\s/.test(token)) return null;
  return token;
}

// Deferred so synthetic mode never initializes or calls App Check. The live
// transport fails closed when the route has no configured App Check instance
// or token acquisition fails.
export async function acquireFirebaseAppCheckToken(): Promise<string | null> {
  try {
    const [{ getToken }, firebaseModule] = await Promise.all([
      import('firebase/app-check'),
      import('../firebase-config'),
    ]);
    if (!firebaseModule.appCheck) return null;
    const result = await getToken(firebaseModule.appCheck, false);
    return normalizeAppCheckToken(result?.token);
  } catch {
    return null;
  }
}

export function buildPropertyQualifierRequest(
  input: Omit<PropertyQualifierRequest, 'schemaVersion' | 'requestId'> & { requestId?: string },
): PropertyQualifierRequest {
  const zipCode = normalizeZipCode(input.zipCode);
  if (!zipCode) {
    throw new PropertyTeaserClientError('INVALID_INPUT', 'Enter a valid 5-digit ZIP Code.');
  }
  if (!isPropertyQualifierMessageKey(input.messageKey)) {
    throw new PropertyTeaserClientError('INVALID_INPUT', 'The selected Report message is unavailable.');
  }
  // Atomic pair check: both halves must be registered together, so a stale
  // version can never ride in under a valid key (or vice versa).
  if (!isPropertyQualifierFlowKey(input.flowKey)
    || input.flowVersion !== PROPERTY_QUALIFIER_FLOW_VERSION) {
    throw new PropertyTeaserClientError(
      'INVALID_INPUT',
      'The Property Check is unavailable for this flow.',
    );
  }

  return {
    schemaVersion: PROPERTY_QUALIFIER_SCHEMA_VERSION,
    requestId: input.requestId?.trim() || createPropertyQualifierRequestId(),
    zipCode,
    messageKey: input.messageKey,
    flowKey: input.flowKey,
    flowVersion: input.flowVersion,
  };
}

function createAbortError(): DOMException {
  return new DOMException('The Property Check was cancelled.', 'AbortError');
}

function waitForSyntheticStage(delayMs: number, signal?: AbortSignal): Promise<void> {
  if (signal?.aborted) return Promise.reject(createAbortError());
  if (delayMs <= 0) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const timerId = globalThis.setTimeout(resolve, delayMs);
    signal?.addEventListener('abort', () => {
      globalThis.clearTimeout(timerId);
      reject(createAbortError());
    }, { once: true });
  });
}

// ZIP reserved for demoing the honest empty state. No real US ZIP Code is
// 00000, so this can never shadow a live area.
const SYNTHETIC_UNAVAILABLE_ZIP = '00000';

// Demo-only place labels so the synthetic preview shows a real locality as the
// result's headline moment instead of a bare "ZIP Code NNNNN". Live mode never
// consults this — the model resolves the actual city/state from its own
// general knowledge (grounding is gone, GH #945). Any ZIP not listed falls
// back to the ZIP label, so the preview stays truthful for arbitrary input.
const SYNTHETIC_PLACE_LABELS: Record<string, string> = {
  '01970': 'Salem, Massachusetts',
};

const syntheticLocationLabel = (zipCode: string): string =>
  SYNTHETIC_PLACE_LABELS[zipCode] ?? `ZIP Code ${zipCode}`;

// One insight per Message destination. Every key in
// PROPERTY_QUALIFIER_MESSAGE_KEYS is covered so no arm falls back to a blank
// callout mid-demo.
const SYNTHETIC_MESSAGE_INSIGHTS: Record<PropertyQualifierMessageKey, string> = {
  adu_standard:
    'Areas like this one typically treat an accessory dwelling as a permitted secondary use, so the practical questions are size, setbacks, and parking rather than whether an ADU is allowed at all.',
  generational_living:
    'Multigenerational households are a common driver of ADU applications here, and occupancy rules in this area generally permit a relative to live in the second unit.',
  imagine_possibility:
    'This area gives you real room to explore: the usual starting question is how much floor area the lot can support once setbacks and lot coverage are applied.',
  lifestyle_studio:
    'A compact studio or one-bedroom footprint tends to be the smoothest path in areas like this, because smaller units usually stay under the size thresholds that trigger extra review.',
  potential_unlocked:
    'The value question in this area usually turns on whether the unit can be permitted as a long-term rental, which is generally allowed where accessory dwellings are permitted.',
  all_roads_lead_home:
    'Whatever brought you here, the same three answers shape the outcome in this area: how big the unit can be, where it can sit on the lot, and what the permit path looks like.',
};

const SYNTHETIC_HIGHLIGHTS = [
  'State law requires local jurisdictions to allow accessory dwelling units on most lots zoned for single-family use.',
  'Local provisions typically cap unit size and set occupancy, setback, and parking requirements that vary by lot.',
  'Final requirements must be verified against the current local ordinance and the permit counter before design work begins.',
];

async function runSyntheticPropertyTeaser(
  request: PropertyQualifierRequest,
  options: PropertyTeaserRunOptions,
): Promise<PropertyQualifierResponse> {
  const delayMs = Math.max(0, options.syntheticStageDelayMs ?? 520);

  for (const stage of PROPERTY_QUALIFIER_STAGES) {
    if (options.signal?.aborted) throw createAbortError();
    options.onStage?.(stage);
    await waitForSyntheticStage(delayMs, options.signal);
  }

  // The empty state is a first-class demo path, not an error: a model call
  // that could not produce a usable ZIP-area answer still returns a
  // well-formed response that says so (GH #945 — grounding is gone, so this
  // no longer means "no public record was found").
  if (request.zipCode === SYNTHETIC_UNAVAILABLE_ZIP) {
    return {
      schemaVersion: PROPERTY_QUALIFIER_SCHEMA_VERSION,
      requestId: request.requestId,
      status: 'unavailable',
      zipCode: request.zipCode,
      messageKey: request.messageKey,
      flowKey: request.flowKey,
      flowVersion: request.flowVersion,
      result: {
        locationLabel: syntheticLocationLabel(request.zipCode),
        headline: 'Public guidance is not available yet',
        summary: 'No usable ZIP-area ADU guidance could be produced for this area right now, so none is shown.',
        highlights: [],
        messageMatchedInsight: null,
        nextStep: 'A full Report researches your specific parcel directly, which does not depend on ZIP-area guidance being published.',
        scopeNote: PROPERTY_QUALIFIER_SCOPE_NOTE,
      },
      sources: [],
      limits: {
        propertySpecific: false,
        parcelVerified: false,
        eligibilityDetermined: false,
        feasibilityDetermined: false,
      },
      meta: {
        executionMode: 'synthetic',
      },
    };
  }

  return {
    schemaVersion: PROPERTY_QUALIFIER_SCHEMA_VERSION,
    requestId: request.requestId,
    status: 'complete',
    zipCode: request.zipCode,
    messageKey: request.messageKey,
    // Echo the request's frozen pair so the synthetic response satisfies the
    // same validator a live response must pass — the fixture is a faithful
    // stand-in for the live shape, not a looser preview-only object.
    flowKey: request.flowKey,
    flowVersion: request.flowVersion,
    result: {
      locationLabel: syntheticLocationLabel(request.zipCode),
      headline: 'ADUs are broadly supported in this area',
      summary: 'General ADU policy knowledge for this ZIP area indicates that accessory dwelling units are allowed on most residentially zoned lots. The limits that matter in practice are unit size, setbacks, and parking, and those vary lot by lot. Confirming them against your own parcel is the next step.',
      highlights: SYNTHETIC_HIGHLIGHTS,
      messageMatchedInsight: SYNTHETIC_MESSAGE_INSIGHTS[request.messageKey],
      nextStep: 'Confirm the size and setback limits that apply to your specific lot, then check whether your parcel has an existing structure that could be converted.',
      scopeNote: PROPERTY_QUALIFIER_SCOPE_NOTE,
    },
    // Grounding is gone (GH #945): every status carries an empty sources
    // array, matching the live contract exactly (see the "faithful stand-in"
    // comment above).
    sources: [],
    limits: {
      propertySpecific: false,
      parcelVerified: false,
      eligibilityDetermined: false,
      feasibilityDetermined: false,
    },
    meta: {
      executionMode: 'synthetic',
    },
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isBoundedText(value: unknown, maxLength: number, allowEmpty = false): value is string {
  return typeof value === 'string'
    && value.length <= maxLength
    && (allowEmpty || value.trim().length > 0);
}

function isSafeSourceUrl(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > 2048) return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

// Matches the exact shape of `.toISOString()`, which is how the backend always
// generates `retrievedAt` (`functions/src/orca/flows/propertyQualifier.ts`) —
// a bounded-length string alone would accept a value that merely looks
// timestamp-shaped rather than one this backend could actually have produced.
const ISO_8601_TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;

function isIsoTimestamp(value: unknown): value is string {
  return typeof value === 'string' && ISO_8601_TIMESTAMP_PATTERN.test(value);
}

function isSafeSource(value: unknown): value is PropertyQualifierSource {
  if (!isRecord(value)) return false;
  return isBoundedText(value.id, 100)
    && isBoundedText(value.publisher, 160)
    && isBoundedText(value.title, 300)
    && isSafeSourceUrl(value.url)
    && isIsoTimestamp(value.retrievedAt);
}

function hasSafeLimits(value: unknown): value is PropertyQualifierResponse['limits'] {
  if (!isRecord(value)) return false;
  return value.propertySpecific === false
    && value.parcelVerified === false
    && value.eligibilityDetermined === false
    && value.feasibilityDetermined === false;
}

export function validatePropertyQualifierResponse(
  value: unknown,
  request: PropertyQualifierRequest,
): PropertyQualifierResponse {
  if (!isRecord(value)
    || value.schemaVersion !== PROPERTY_QUALIFIER_SCHEMA_VERSION
    || value.requestId !== request.requestId
    || value.messageKey !== request.messageKey
    || value.zipCode !== request.zipCode
    // The response must echo the request's flow pair exactly. A response that
    // renames its own arm would silently misattribute the funnel.
    || value.flowKey !== request.flowKey
    || value.flowVersion !== request.flowVersion
    || !PROPERTY_QUALIFIER_STATUSES.includes(value.status as PropertyQualifierStatus)
    || !isRecord(value.result)
    || !isBoundedText(value.result.locationLabel, 160)
    || !isBoundedText(value.result.headline, 180)
    || !isBoundedText(value.result.summary, PROPERTY_QUALIFIER_MAX_SUMMARY_LENGTH)
    || !Array.isArray(value.result.highlights)
    || value.result.highlights.length > 3
    || !value.result.highlights.every((highlight) => isBoundedText(highlight, 360))
    || !(value.result.messageMatchedInsight === null
      || isBoundedText(value.result.messageMatchedInsight, 600))
    || !isBoundedText(value.result.nextStep, 400)
    || value.result.scopeNote !== PROPERTY_QUALIFIER_SCOPE_NOTE
    || !Array.isArray(value.sources)
    || value.sources.length > 8
    || !value.sources.every(isSafeSource)
    || !hasSafeLimits(value.limits)) {
    throw new PropertyTeaserClientError(
      'INVALID_RESPONSE',
      'Property Check returned an unsupported response.',
    );
  }

  // Grounding is gone (GH #945): the qualifier never produces citations, so
  // sources must ALWAYS be empty regardless of status. Asserting that here —
  // rather than only per-status — mirrors the backend's own projection gate:
  // a future re-grounding of this flow is a deliberate contract change (an
  // explicit edit to this check), never a silent drift back on via a
  // response that merely happens to carry sources.
  if (value.sources.length > 0
    // Mirrors the backend's own derivation (`status = highlights.length > 0 ?
    // "complete" : "partial"`): 'complete' requires at least one highlight,
    // 'partial' means a valid payload with none.
    || (value.status === 'complete' && value.result.highlights.length === 0)
    || (value.status === 'partial' && value.result.highlights.length > 0)
    || (value.status === 'unavailable'
      && (value.result.highlights.length > 0 || value.result.messageMatchedInsight !== null))) {
    throw new PropertyTeaserClientError(
      'INVALID_RESPONSE',
      'Property Check returned an unsupported response.',
    );
  }

  return value as unknown as PropertyQualifierResponse;
}

async function runLivePropertyTeaser(
  request: PropertyQualifierRequest,
  endpoint: string,
  fetchImpl: FetchLike,
  appCheckTokenProvider: PropertyTeaserAppCheckTokenProvider,
  options: PropertyTeaserRunOptions,
): Promise<PropertyQualifierResponse> {
  if (!SAFE_ENDPOINT_PATTERN.test(endpoint)) {
    throw new PropertyTeaserClientError('INVALID_INPUT', 'Property Check endpoint is unavailable.');
  }

  options.onStage?.('resolving_zip');
  let appCheckToken: string | null;
  try {
    appCheckToken = normalizeAppCheckToken(await appCheckTokenProvider());
  } catch {
    appCheckToken = null;
  }
  if (options.signal?.aborted) throw createAbortError();
  if (!appCheckToken) {
    throw new PropertyTeaserClientError(
      'REQUEST_FAILED',
      'Property Check could not connect. Try again.',
    );
  }

  options.onStage?.('researching_official_sources');
  let response: Response;
  try {
    response = await fetchImpl(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Request-Id': request.requestId,
        'X-Firebase-AppCheck': appCheckToken,
      },
      body: JSON.stringify(request),
      signal: options.signal,
      credentials: 'same-origin',
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new PropertyTeaserClientError('REQUEST_FAILED', 'Property Check could not connect. Try again.');
  }

  if (!response.ok) {
    throw new PropertyTeaserClientError('REQUEST_FAILED', 'Property Check is temporarily unavailable. Try again.');
  }

  options.onStage?.('validating_evidence');
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    throw new PropertyTeaserClientError('INVALID_RESPONSE', 'Property Check returned an unsupported response.');
  }

  return validatePropertyQualifierResponse(body, request);
}

export function createPropertyTeaserClient({
  mode = 'synthetic',
  endpoint = '/api/propertyQualifier',
  fetchImpl = globalThis.fetch,
  appCheckTokenProvider = acquireFirebaseAppCheckToken,
}: PropertyTeaserClientConfig = {}): PropertyTeaserClient {
  return {
    mode,
    async run(input, options = {}) {
      const request = buildPropertyQualifierRequest(input);
      if (mode === 'synthetic') return runSyntheticPropertyTeaser(request, options);
      if (typeof fetchImpl !== 'function') {
        throw new PropertyTeaserClientError('REQUEST_FAILED', 'Property Check could not connect. Try again.');
      }
      return runLivePropertyTeaser(
        request,
        endpoint,
        fetchImpl,
        appCheckTokenProvider,
        options,
      );
    },
  };
}

// Dev-only App Check escape hatch. Local `npm run dev` has no reCAPTCHA
// provider, so the live transport would fail closed at token acquisition and the
// live path could never be exercised outside a deployed environment. The knob is
// honored ONLY when the caller proves a development build: any production,
// preview, or staging bundle takes the real Firebase provider regardless of what
// the environment sets, so a leaked var cannot forge an App Check assertion.
export function resolveAppCheckTokenProvider(
  devToken: unknown,
  isDevBuild: boolean,
): PropertyTeaserAppCheckTokenProvider {
  if (!isDevBuild) return acquireFirebaseAppCheckToken;
  const token = normalizeAppCheckToken(devToken);
  if (!token) return acquireFirebaseAppCheckToken;
  return async () => token;
}

// Dev-only synthetic-mode escape hatch, mirroring resolveAppCheckTokenProvider
// above: local `npm run dev` has no live backend readily available, so
// synthetic is a reasonable default there. But that default must never leak —
// a missing or misconfigured VITE_PROPERTY_QUALIFIER_MODE env var on a
// production, preview, or staging bundle must not silently serve fixture
// content to real users. The knob is honored ONLY when the caller proves a
// development build; every non-dev bundle takes live mode regardless of what
// the environment sets.
export function resolvePropertyTeaserMode(
  requestedMode: unknown,
  isDevBuild: boolean,
): PropertyTeaserMode {
  if (!isDevBuild) return 'live';
  return requestedMode === 'live' ? 'live' : 'synthetic';
}

// This repository has no vite-env.d.ts; typed files use the established cast
// at the Vite environment seam (see landingAttribution.ts).
const viteEnv = (import.meta as any).env as Record<string, string | undefined> | undefined;
// Read separately from `viteEnv`: Vite exposes DEV as a real boolean, and the
// record cast above narrows every value to `string | undefined`.
const isDevBuild = (import.meta as any).env?.DEV === true;
const configuredMode = resolvePropertyTeaserMode(viteEnv?.VITE_PROPERTY_QUALIFIER_MODE, isDevBuild);

export const propertyTeaserClient = createPropertyTeaserClient({
  mode: configuredMode,
  endpoint: viteEnv?.VITE_PROPERTY_QUALIFIER_ENDPOINT || '/api/propertyQualifier',
  appCheckTokenProvider: resolveAppCheckTokenProvider(
    viteEnv?.VITE_PROPERTY_QUALIFIER_DEV_APPCHECK_TOKEN,
    isDevBuild,
  ),
});
